import { LoadingButton } from '@mui/lab';
import { Stack, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '../../../hooks/useUser';
import { parseApiError } from '../../../utils/parseApiError';
import { ocppService } from '../../../services/ocpp/OcppService';
import { CustomInput } from '../../common/CustomInput/CustomInput';
import { CustomTelInput } from '../../common/CustomTelInput/CustomInput';
import { Loading } from '../../common/Loading/Loading';
import { SnackBar } from '../../common/SnackBar/SnackBar';
import { StyledForm } from '../../common/StyledForm/StyledForm';
import { validationStationRequestSchema } from './validationSchema';
import { toMaskedUaPhone } from '../../../utils/phone';

export const StationRequestForm = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showSnackBar, setShowSnackBar] = useState('');
  const [serverError, setServerError] = useState('');

  const { t } = useTranslation();
  const { user } = useUser();

  useEffect(() => {
    setIsLoading(false);
  }, [user]);

  const {
    values,
    errors,
    handleSubmit,
    touched,
    setFieldValue,
    setFieldTouched,
    resetForm,
  } = useFormik({
    initialValues: {
      phone: toMaskedUaPhone(user?.phone),
      city: '',
      comment: '',
    },
    initialTouched: {
      phone: false,
      city: false,
      comment: false,
    },
    validationSchema: validationStationRequestSchema(t),
    onSubmit: async (values) => {
      setServerError('');
      try {
        await ocppService.createStationRequest(values);
        setShowSnackBar(t('stationsRequestPage.sendRequest'));
        resetForm();
      } catch (error) {
        Sentry.captureException(error);
        if (error instanceof AxiosError) {
          setServerError(
            parseApiError(error, t('stationsRequestPage.serverError')).message,
          );
        } else {
          setServerError(t('stationsRequestPage.serverError'));
        }
      }
    },
  });

  const handleChangePhone = (newValue: string) => {
    setFieldValue('phone', newValue, true);
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <StyledForm onSubmit={handleSubmit}>
      <Typography variant="h1" textAlign="center" color="common.white">
        {t('stationsRequestPage.title')}
      </Typography>
      <Stack direction="column" sx={{ marginTop: '30px', gap: '15px' }}>
        <Typography color="common.white">
          {t('stationsRequestPage.phoneLabel')}
        </Typography>
        <CustomTelInput
          fieldName={'phone'}
          label={t('stationsRequestPage.phonePlaceholder')}
          value={values.phone || ''}
          onChange={(value) => {
            setFieldTouched('phone');
            handleChangePhone(value);
          }}
          error={touched['phone'] && errors.phone}
        />
      </Stack>
      <Stack direction="column" sx={{ marginTop: '10px', gap: '15px' }}>
        <Typography color="common.white">
          {t('stationsRequestPage.cityLabel')}
        </Typography>
        <CustomInput
          fieldName={'city'}
          autoComplete="address-level2"
          enterKeyHint="next"
          label={t('stationsRequestPage.cityPlaceholder')}
          value={values.city || ''}
          onChange={(e) => {
            setFieldTouched('city');
            setFieldValue('city', e.target.value);
          }}
          error={touched['city'] && errors.city}
        />
      </Stack>
      <Stack direction="column" sx={{ marginTop: '10px', gap: '15px' }}>
        <Typography color="common.white">
          {t('stationsRequestPage.commentLabel')}
        </Typography>
        <CustomInput
          fieldName={'comment'}
          autoComplete="off"
          label={t('stationsRequestPage.commentPlaceholder')}
          value={values.comment || ''}
          onChange={(e) => {
            setFieldTouched('comment');
            setFieldValue('comment', e.target.value);
          }}
          multiline
          maxRows={4}
        />
      </Stack>

      <Stack
        direction="column"
        sx={{ marginTop: '30px', position: 'relative' }}
      >
        <LoadingButton
          variant="contained"
          color="primary"
          type="submit"
          sx={{
            flex: '1 1 auto',
            padding: '10px',
            maxHeight: '50px',
            width: '100%',
          }}
          disabled={false}
        >
          {t('stationsRequestPage.submitBtnText')}
        </LoadingButton>
        {serverError && (
          <Typography
            variant="body2"
            color="red.main"
            sx={{
              position: 'absolute',
              top: '-70%',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            {serverError}
          </Typography>
        )}
        <SnackBar showSnackBar={Boolean(showSnackBar)} message={showSnackBar} />
      </Stack>
    </StyledForm>
  );
};
